import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { inventoryService } from '@/services';
import { queryKeys } from '@/services/query-keys';
import { useInventoryFilterStore } from '@/stores';
import { getDefaultStateId, isPinnedToFollowedState } from '@/utils/inventory/intraday-state-utils';

interface UseInventoryStatePollingOptions {
  enabled?: boolean;
  pollInterval?: number;
  onNewStateDetected?: (newStateId: number) => void;
}

export const useInventoryStatePolling = (options?: UseInventoryStatePollingOptions) => {
  const { enabled = true, pollInterval = 45000, onNewStateDetected } = options || {};
  const queryClient = useQueryClient();
  const inventoryFilters = useInventoryFilterStore((state) => state.inventoryFilters);
  const setInventoryFilters = useInventoryFilterStore((state) => state.setInventoryFilters);
  const lastFollowedStateIdRef = useRef<number | null>(null);
  const { data: statesData } = useQuery({
    queryKey: queryKeys.inventory.states(),
    queryFn: () => inventoryService.getInventoryStates(),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval: enabled ? pollInterval : false,
  });
  useEffect(() => {
    if (!statesData || statesData.length === 0) return;
    // "Latest" = the newest snapshot of any type (getDefaultStateId), so auto-follow
    // tracks whatever the default view shows — a live intraday during the day or a
    // frozen evening EOD snapshot after the close (LR-503).
    const currentNewestId = getDefaultStateId(statesData);
    if (currentNewestId === undefined) return;
    if (lastFollowedStateIdRef.current !== null && currentNewestId !== lastFollowedStateIdRef.current) {
      // Only an explicit pin to the former-newest needs a bump; the default view
      // (undefined) auto-follows via InventoryPage's `inventoryFilters.state ||
      // defaultStateId`, and a historical pin stays put (isPinnedToFollowedState).
      if (isPinnedToFollowedState(inventoryFilters.state, lastFollowedStateIdRef.current)) {
        queryClient.invalidateQueries({ queryKey: [...queryKeys.inventory.all] });
        setInventoryFilters({ ...inventoryFilters, state: currentNewestId });
        if (onNewStateDetected) {
          onNewStateDetected(currentNewestId);
        }
      }
      lastFollowedStateIdRef.current = currentNewestId;
    } else if (lastFollowedStateIdRef.current === null) {
      lastFollowedStateIdRef.current = currentNewestId;
    }
  }, [statesData, inventoryFilters, queryClient, setInventoryFilters, onNewStateDetected]);
  return {
    states: statesData,
  };
};

